import React, { createContext, useContext, useState, useCallback } from 'react'

interface CommentModalState {
  isOpen: boolean
  messageIndex: number | null
  initialComment: string
}

interface CommentModalContextValue {
  openCommentModal: (messageIndex: number, initialComment?: string) => void
  closeCommentModal: () => void
  modalState: CommentModalState
  saveComment: (comment: string) => void
  onSaveComment?: (messageIndex: number, comment: string) => void
  setOnSaveComment: (callback: (messageIndex: number, comment: string) => void) => void
}

const CommentModalContext = createContext<CommentModalContextValue | undefined>(undefined)

export const useCommentModal = (): CommentModalContextValue => {
  const context = useContext(CommentModalContext)
  if (!context) {
    throw new Error('useCommentModal must be used within a CommentModalProvider')
  }
  return context
}

interface CommentModalProviderProps {
  children: React.ReactNode
}

export const CommentModalProvider: React.FC<CommentModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<CommentModalState>({
    isOpen: false,
    messageIndex: null,
    initialComment: '',
  })
  const [onSaveComment, setOnSaveComment] = useState<
    ((messageIndex: number, comment: string) => void) | undefined
  >(undefined)

  const openCommentModal = useCallback((messageIndex: number, initialComment: string = '') => {
    setModalState({
      isOpen: true,
      messageIndex,
      initialComment,
    })
  }, [])

  const closeCommentModal = useCallback(() => {
    setModalState({
      isOpen: false,
      messageIndex: null,
      initialComment: '',
    })
  }, [])

  const saveComment = useCallback(
    (comment: string) => {
      if (modalState.messageIndex !== null && onSaveComment) {
        onSaveComment(modalState.messageIndex, comment)
      }
      closeCommentModal()
    },
    [modalState.messageIndex, onSaveComment, closeCommentModal]
  )

  const setOnSaveCommentCallback = useCallback(
    (callback: (messageIndex: number, comment: string) => void) => {
      setOnSaveComment(() => callback)
    },
    []
  )

  const contextValue: CommentModalContextValue = {
    openCommentModal,
    closeCommentModal,
    modalState,
    saveComment,
    onSaveComment,
    setOnSaveComment: setOnSaveCommentCallback,
  }

  return (
    <CommentModalContext.Provider value={contextValue}>{children}</CommentModalContext.Provider>
  )
}
